/**
 * 工具函数集合
 */

import { isEqual } from 'lodash-es';
import { useRef } from 'react';
import { ITaskStep, EStepStatus } from '../../component/TaskAnalysis';

/**
 * 任务状态类型
 */
export type TaskStatus = 'processed' | 'failed';

/**
 * 阶段标识类型
 */
export type StageType = 'stage1' | 'stage2' | 'stage3' | 'stage4';

/**
 * 回调数据接口
 * 单个 callback 对象中的 callback_data 字段
 */
export interface ICallbackData {
  /** 中文描述文本 */
  cn?: string;
  /** 英文描述文本 */
  en?: string;
  /** 交付时间 */
  delivery_time?: string;
  /** 外部任务 ID */
  external_task_id?: string;
}

/**
 * 任务回调接口
 * API 返回的回调数组中的单个元素
 */
export interface ITaskCallback {
  /** 回调数据 */
  callback_data: ICallbackData;
  /** 阶段标识 */
  stage: StageType;
  /** 任务状态 */
  status: TaskStatus;
  /** 创建时间 */
  create_time: string;
}

/**
 * 深度比较两个回调数组是否相等
 * 只比较关键字段以提高性能
 *
 * @param arr1 - 第一个回调数组
 * @param arr2 - 第二个回调数组
 * @returns true 表示两个数组相同，false 表示不同（只比较 stage、status 和 callback_data）
 */
export function deepCompareCallbacks(
  arr1: ITaskCallback[],
  arr2: ITaskCallback[],
): boolean {
  if (arr1.length !== arr2.length) return false;

  return arr1.every((item1, index) => {
    const item2 = arr2[index];

    // 如果两个都不是对象，直接比较
    if (typeof item1 !== 'object' || typeof item2 !== 'object') {
      return item1 === item2;
    }

    // 如果其中一个是 null，比较是否都是 null
    if (item1 === null || item2 === null) {
      return item1 === item2;
    }

    // 对于对象，只比较关键字段以提高性能
    // callback_data 现在是对象，需要深度比较
    const dataEqual = isEqual(item1.callback_data, item2.callback_data);
    return (
      item1.stage === item2.stage && item1.status === item2.status && dataEqual
    );
  });
}

/**
 * 深度比较两个步骤数组是否相等
 * 只比较关键字段以提高性能
 *
 * @param arr1 - 第一个步骤数组
 * @param arr2 - 第二个步骤数组
 * @returns true 表示两个数组相同，false 表示不同
 */
export function deepCompareSteps(
  arr1: ITaskStep[],
  arr2: ITaskStep[],
): boolean {
  if (arr1.length !== arr2.length) return false;

  return arr1.every((item1, index) => {
    const item2 = arr2[index];
    return (
      item1.key === item2.key &&
      item1.status === item2.status &&
      isEqual(item1.description, item2.description) &&
      item1.deliveryTime === item2.deliveryTime
    );
  });
}

/**
 * 检查任务状态是否为完成状态
 *
 * @param taskStatus - 任务状态字符串
 * @returns 是否为完成状态（processed）
 */
export function isTaskCompleted(taskStatus: string): boolean {
  return taskStatus === 'processed';
}

/**
 * 检查任务状态是否为失败状态
 *
 * @param taskStatus - 任务状态字符串
 * @returns 是否为失败状态（failed）
 */
export function isTaskFailed(taskStatus: string): boolean {
  return taskStatus === 'failed';
}

/**
 * 深度比较记忆化 Hook
 * 返回一个稳定的引用，只有当值发生深度变化时才会更新
 *
 * @param value - 需要记忆化的值
 * @returns 值的稳定引用
 * @template T - 值的类型
 */
export function useDeepCompareMemo<T>(value: T): T {
  const ref = useRef<T>(value);
  if (!isEqual(value, ref.current)) {
    ref.current = value;
  }
  return ref.current;
}

/**
 * 步骤配置接口
 * 定义每个步骤的静态配置信息
 */
export interface IStepConfig {
  /** 步骤的唯一标识，对应 API 返回的 stage 值 */
  key: string;
  /** 步骤标题，显示在 UI 中的主要文本 */
  title: string;
  /** 步骤副标题，显示在标题下方的辅助说明 */
  subTitle: string;
}

/**
 * 步骤配置常量
 * 用于映射 API 返回的 stage 到 UI 步骤
 * 包含四个主要阶段：数据选择、定义优化、主题标注、质量检查
 */
export const STEP_CONFIGS: IStepConfig[] = [
  {
    key: 'stage1',
    title: 'Data Selection:',
    subTitle: 'Set tagging time range and filter conditions',
  },
  {
    key: 'stage2',
    title: 'Definition Optimization:',
    subTitle: 'Refine topic definitions based on real game data',
  },
  {
    key: 'stage3',
    title: 'Topic Tagging:',
    subTitle: 'Tag filtered data with topics',
  },
  {
    key: 'stage4',
    title: 'Quality Check:',
    subTitle: 'Automatically verify tagging accuracy',
  },
];

/**
 * 数据转换函数
 * 将 API 返回的 callbacks 转换为组件可用的步骤数组
 *
 * 转换逻辑:
 * 1. 验证输入是否为数组，非数组则返回空数组
 * 2. 创建 stage 到 callback 的映射以提高查找效率
 * 3. 遍历预定义的步骤配置（STEP_CONFIGS）
 * 4. 对于每个配置，通过 stage 查找对应的 callback
 * 5. 从 callback_data 中提取中英文文本描述
 * 6. 创建步骤对象，保留 callback 的原始状态和描述
 * 7. 过滤掉未找到 callback 的步骤（callback 为 undefined 时）
 *
 * @param callbacks - API 返回的回调数组，每个 callback 包含 stage、status 和 callback_data
 * @returns 转换后的步骤数组，包含所有有对应 callback 的步骤
 */
export function transformCallbacksToSteps(
  callbacks: ITaskCallback[] = [],
): ITaskStep[] {
  /**
   * 从 callback_data 中提取中英文文本
   * 返回包含 zh 和 en 两个字段的对象
   *
   * @param callbackData - 回调数据对象
   * @returns 包含中英文文本的对象，如果输入无效则返回空字符串对象
   */
  // eslint-disable-next-line
  const getCallbackText: (callbackData: ICallbackData) => {
    zh: string;
    en: string;
  } = callbackData => {
    if (typeof callbackData === 'object' && callbackData !== null) {
      return {
        zh: callbackData.cn || callbackData.en || '',
        en: callbackData.en || callbackData.cn || '',
      };
    }
    return { zh: '', en: '' };
  };

  if (!Array.isArray(callbacks)) return [];

  // 创建 stage 到 callback 的映射
  const callbackMap = new Map<StageType, ITaskCallback>(
    callbacks.map(cb => [cb.stage as StageType, cb]),
  );

  // API 状态到组件状态的映射
  const statusMap: Record<TaskStatus, EStepStatus> = {
    processed: EStepStatus.COMPLETED,
    failed: EStepStatus.FAILED,
  };

  // 遍历 STEP_CONFIGS，处理 processed 和 failed 状态的 callback
  // 如果前面的步骤都完成了，自动添加下一步（只添加一个）
  const steps: ITaskStep[] = [];
  let canAddNext = true; // 控制是否可以添加下一个待处理步骤

  for (const config of STEP_CONFIGS) {
    const callback = callbackMap.get(config.key as any);

    if (callback) {
      // 有 callback，使用实际状态
      const description = getCallbackText(callback.callback_data);
      steps.push({
        key: config.key,
        title: config.title,
        subTitle: config.subTitle,
        status: statusMap[callback.status],
        description,
        deliveryTime: callback?.callback_data?.delivery_time,
      });

      // 如果当前步骤失败，阻止添加后续步骤
      if (callback.status === 'failed') {
        canAddNext = false;
      }
      // processed 状态保持 canAddNext 不变，允许继续添加
    } else if (canAddNext) {
      // 没有 callback 且可以添加，添加待处理步骤
      steps.push({
        key: config.key,
        title: config.title,
        subTitle: config.subTitle,
        status: EStepStatus.RUNNING,
        description: { zh: '', en: '' },
        deliveryTime: undefined,
      });
      // 添加后立即阻止继续添加后续步骤
      canAddNext = false;
    }
    // 如果没有 callback 且 canAddNext 为 false，不做任何操作
  }
  console.log(steps, callbackMap);
  return steps;
}
